import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { pathToRef, saveAppRef, buildPlayStoreUrl } from "@/lib/appRef";
import { normalizePromo, isBannerDue, markBannerShown, dismissBanner, isQuietMoment } from "@/lib/appPromo";

/**
 * Get-the-App bar — compact (56px), phone-only, env-gated, and governed by
 * admin App Promotion Controls (on/off, frequency, dismissal duration).
 * Fixed at the top; publishes --rgm-app-banner-h so navs + content shift
 * down instead of being covered.
 */
const BAR_H = "56px";
const TICK_MS = 30000;
// Immersive full-screen surfaces (live rooms, studio) — never shown.
const HIDDEN_PREFIXES = ["/beams/", "/studio"];

let settingsPromise = null;
const loadPromoSettings = () => {
  if (!settingsPromise) {
    settingsPromise = api.get("/platform/app-promo")
      .then((r) => normalizePromo(r.data))
      .catch(() => normalizePromo(null));
  }
  return settingsPromise;
};
export const _resetPromoSettingsCache = () => { settingsPromise = null; };

const useIsPhone = () => {
  const q = "(max-width: 767px)";
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia?.(q).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(q);
    if (!mq) return undefined;
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return !!phone;
};

const GetTheAppBanner = () => {
  const url = process.env.REACT_APP_PLAY_STORE_URL;
  const location = useLocation();
  const isPhone = useIsPhone();
  const [settings, setSettings] = useState(null);
  const [shown, setShown] = useState(false);
  const [tick, setTick] = useState(0);

  const { ref, path, ctaUrl } = useMemo(() => {
    const p = (location && location.pathname) || "/";
    const r = pathToRef(p);
    return { ref: r, path: p, ctaUrl: buildPlayStoreUrl(url, { ref: r, path: p }) };
  }, [url, location]);

  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    loadPromoSettings().then((s) => { if (alive) setSettings(s); });
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => { alive = false; clearInterval(id); };
  }, [url]);

  const onImmersive = HIDDEN_PREFIXES.some((pre) => path.startsWith(pre));

  // Decide (re)appearance. A visible bar stays; new pop-ups wait for a
  // non-quiet moment (no contest page, no music/video playing).
  useEffect(() => {
    if (!settings || shown) return;
    const now = Date.now();
    if (!isBannerDue(settings, now, sessionStorage, localStorage)) return;
    if (isQuietMoment(path)) return;
    markBannerShown(settings, now, sessionStorage, localStorage);
    setShown(true);
  }, [settings, shown, path, tick]);

  useEffect(() => {
    if (settings && !settings.banner_enabled) setShown(false);
  }, [settings]);

  const visible = !!url && shown && isPhone && !onImmersive;

  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty("--rgm-app-banner-h", visible ? BAR_H : "0px");
    return () => root.setProperty("--rgm-app-banner-h", "0px");
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    dismissBanner(settings, Date.now(), sessionStorage, localStorage);
    setShown(false);
  };

  return (
    <div
      data-testid="get-the-app-banner"
      className="md:hidden fixed left-0 right-0 z-[56] h-14 bg-[#0b0b10]/95 backdrop-blur-md border-b border-amber-400/15"
      style={{ top: "var(--rgm-banner-h, 0px)" }}
    >
      <div className="h-full flex items-center gap-2.5 pl-3 pr-1.5">
        <img
          src="/rgm-play-icon.png"
          alt="RGM Studio app icon"
          width={36}
          height={36}
          data-testid="get-the-app-icon"
          className="shrink-0 w-9 h-9 rounded-[9px] ring-1 ring-white/10"
        />
        <div className="flex-1 min-w-0 leading-tight">
          <p className="text-white font-semibold text-[13px] truncate">RGM Studio</p>
          <p className="text-gray-400 text-[11px] truncate">Get the official app</p>
        </div>
        <a
          href={ctaUrl || url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="get-the-app-cta"
          data-ref={ref}
          className="shrink-0 h-8 px-3.5 inline-flex items-center rounded-full bg-amber-400 hover:bg-amber-300 active:bg-amber-500 text-black text-[12px] font-bold transition-colors"
          onClick={() => {
            saveAppRef({ ref, path });
            dismiss();
          }}
        >
          Get App
        </a>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss app banner"
          data-testid="get-the-app-dismiss"
          className="shrink-0 w-8 h-8 rounded-full text-gray-500 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default GetTheAppBanner;
